import Vue from 'vue';
import VueRouter from 'vue-router';

import AccountRouter from '@modules/Account/router';
import WebpageRouter from '@modules/Webpages/router';
import DomainRouter from '@modules/Domain/router';
import BlackoutPeriodRouter from '@modules/BlackoutPeriod/router';
import BatchJobsRouter from '@modules/BatchJobs/router';
import IncidentRouter from '@modules/Incident/router';
import TagsRouter from '@modules/Tags/router';
import NotificationRouter from '@modules/Notification/router';
import ContactRouter from '@modules/Contact/router';
import ContactGroupRouter from '@modules/ContactGroup/router';
import TelegramNotificationRouter from '@modules/TelegramNotification/router';
import PolicyRouter from '@modules/Policy/router';
import AiTriageRouter from '@modules/AiTriage/router';
import { aiTriageBeforeEnter } from '@modules/AiTriage/router/aiTriageRouteGuard';
import AutoFinetuneRouter from '@modules/AutoFinetune/router';
import { AUTO_FINETUNE_ROUTE_NAMES } from '@modules/AutoFinetune/router/routes';
import PollingHistoryRouter from '@modules/PollingHistory/router';
import TrafficConfigRouter from '@modules/TrafficConfig/router';
import DomainMonitorRouter from '@modules/DomainMonitor/router';
import UserManagementRouter from '@modules/UserManagement/router';
import CustomElementRouter from '@modules/CustomElement/router';
import WebCrawlRouter from '@modules/WebCrawl/router';
import ChangeEventsRouter from '@modules/ChangeEvents/router';
import LicensingRouter from '@modules/Licensing/router';

import Home from '@modules/Home/Home.vue';
import AdminRouter from '@modules/Admin/router';
import JwtService from '@shared/services/jwt.service';
import store from '../store';
import { waitForUserData } from '../store/user';
Vue.use(VueRouter);

const NestedView = {
  name: 'NestedView',
  render(h) {
    return h('router-view');
  }
};

const routes = [
  {
    path: '/',
    redirect: '/home'
  },
  {
    path: '/home',
    component: Home,
    meta: {
      title: 'Home',
      role: ['User']
    }
  },
  {
    path: '/admin',
    component: NestedView,
    children: AdminRouter,
    meta: {
      title: 'Admin',
      role: ['User']
    }
  },
  {
    path: '/webpages',
    component: NestedView,
    children: WebpageRouter,
    meta: {
      title: 'Webpages',
      role: ['User']
    }
  },
  {
    path: '/domain',
    component: NestedView,
    children: DomainRouter,
    meta: {
      title: 'Domain',
      role: ['User']
    }
  },
  {
    path: '/batch-jobs',
    component: NestedView,
    children: BatchJobsRouter,
    meta: {
      title: 'Batch Jobs',
      role: ['User']
    }
  },
  {
    path: '/domain-monitoring',
    component: NestedView,
    children: DomainMonitorRouter,
    meta: {
      title: 'SSL/Whois/Dns Monitoring',
      role: ['User']
    }
  },
  {
    path: '/traffic-config',
    component: NestedView,
    children: TrafficConfigRouter,
    meta: {
      title: 'Traffic Configuration',
      role: ['User']
    }
  },
  {
    path: '/incident',
    component: NestedView,
    children: IncidentRouter,
    meta: {
      title: 'Incident',
      role: ['User']
    }
  },
  {
    path: '/tags',
    component: NestedView,
    children: TagsRouter,
    meta: {
      title: 'Tags',
      role: ['User']
    }
  },
  {
    path: '/user-management',
    component: NestedView,
    children: UserManagementRouter,
    meta: {
      title: 'UserManagement',
      role: ['User'],
      showWhenFeatureEnabled: 'cloudsine-staff'
    }
  },
  {
    path: '/polling-history',
    component: NestedView,
    children: PollingHistoryRouter,
    meta: {
      title: 'Polling History',
      role: ['User']
    }
  },
  {
    path: '/blackout-periods',
    component: NestedView,
    children: BlackoutPeriodRouter,
    meta: {
      title: 'Blackout Periods',
      role: ['User']
    }
  },
  {
    path: '/notification',
    component: NestedView,
    children: NotificationRouter,
    meta: {
      title: 'Notification',
      role: ['User']
    }
  },
  {
    path: '/policy',
    component: NestedView,
    children: PolicyRouter,
    meta: {
      title: 'Policy',
      role: ['User']
    }
  },
  {
    path: '/ai-triage',
    component: NestedView,
    children: AiTriageRouter,
    beforeEnter: aiTriageBeforeEnter,
    meta: {
      title: 'AI Triage',
      role: ['User']
    }
  },
  {
    path: '/auto-finetune',
    name: AUTO_FINETUNE_ROUTE_NAMES.ROOT,
    component: NestedView,
    children: AutoFinetuneRouter,
    meta: {
      title: 'Auto Finetune',
      role: ['User']
    }
  },
  {
    path: '/contact',
    component: NestedView,
    children: ContactRouter,
    meta: {
      title: 'Contact',
      role: ['User']
    }
  },
  {
    path: '/contact-group',
    component: NestedView,
    children: ContactGroupRouter,
    meta: {
      title: 'Contact Group',
      role: ['User']
    }
  },
  {
    path: '/telegram-notification',
    component: NestedView,
    children: TelegramNotificationRouter,
    meta: {
      title: 'Telegram Notification',
      role: ['User']
    }
  },
  {
    path: '/activity-log',
    component: () => import('@modules/ActivityLog/components/ActivityLogList'),
    meta: {
      title: 'Activity Log',
      role: ['User']
    }
  },
  {
    path: '/login',
    component: () => import('@modules/Authentication/components/SignIn.vue'),
    meta: {
      isPublic: true
    }
  },
  {
    path: '/forgot-password',
    component: () => import('@modules/Authentication/components/ForgotPassword.vue'),
    meta: {
      isPublic: true
    }
  },
  {
    path: '/reset-password',
    component: () => import('@modules/Authentication/components/ResetPassword.vue'),
    meta: {
      isPublic: true
    }
  },
  {
    path: '/email-rebaseline/:code',
    component: () => import('@modules/EmailRebaseline/EmailRebaseline.vue'),
    meta: {
      isPublic: true
    }
  },
  {
    path: '/account',
    component: NestedView,
    children: AccountRouter,
    meta: {
      title: 'Account',
      role: ['User']
    }
  },
  {
    path: '/custom-element',
    component: NestedView,
    children: CustomElementRouter,
    meta: {
      title: 'Custom Element',
      role: ['User']
    }
  },
  {
    path: '/web-crawl',
    component: NestedView,
    children: WebCrawlRouter,
    meta: {
      title: 'Website Crawl',
      role: ['User'],
      showWhenFeatureEnabled: 'firecrawl',
    }
  },
  {
    path: '/change-events',
    component: () => import('@modules/ChangeEvents/ChangeEvents.vue'),
    children: ChangeEventsRouter,
    meta: {
      title: 'Aggregated Alerts',
      role: ['User'],
      showWhenFeatureEnabled: 'change-events',
    }
  },
  {
    path: '/licensing',
    component: NestedView,
    children: LicensingRouter,
    meta: {
      title: 'Licensing',
      role: ['User']
    }
  },
  {
    path: '/unauthorized',
    component: () => import('@shared/components/Unauthorized.vue'),
    meta: {
      role: ['User']
    }
  },
  {
    path: '/404',
    component: () => import('@shared/components/PageNotFound.vue'),
    meta: {
      title: '404',
      role: ['User']
    }
  },
  { path: '*', redirect: '/404' }
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  store
});

router.beforeEach(async (to, from, next) => {
  const isLogged = JwtService.getToken();

  if (to.meta.isPublic) {
    next();
  } else if (!isLogged) {
    next({
      path: '/login',
      query: { redirect: to.fullPath }
    });
  } else {
    const recordsWithRoles = to.matched.filter(
      (record) => record.meta && Array.isArray(record.meta.role) && record.meta.role.length
    );

    if (recordsWithRoles.length === 0) {
      next({ path: '/unauthorized' });
      return;
    }

    const isAuthorized = recordsWithRoles.every((record) =>
      record.meta.role.includes('User')
    );
    if (!isAuthorized) {
      next({ path: '/unauthorized' });
      return;
    }

    const featureRecord = to.matched.find((r) => r.meta && r.meta.showWhenFeatureEnabled);
    if (featureRecord) {
      const featureKey = featureRecord.meta.showWhenFeatureEnabled;
      let fa = store.state.user && store.state.user.data && store.state.user.data.feature_access;
      if (!fa) {
        try {
          const userData = await Promise.race([
            waitForUserData(),
            new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 10000)),
          ]);
          fa = userData && userData.feature_access;
        } catch {
          next({ path: '/home' });
          return;
        }
      }
      if (!fa || !fa[featureKey]) {
        next({ path: '/home' });
        return;
      }
    }
    next();
  }
});

export default router;
